/* PLP filter popup.
 *
 * Options are rendered server-side from `collection.filters` (Shopify native
 * storefront filters, configured in the Search & Discovery app). The markup is a
 * real GET form: tick some options, press Apply, and the browser loads the
 * filtered URL. Clear is a plain link back to the unfiltered URL.
 *
 * Both buttons work with JavaScript disabled — Apply is a submit, Clear is a
 * link. JS only adds the niceties: the "(NN selected)" count, the mobile
 * accordion, and resetting the tick boxes when Clear is pressed.
 *
 * No AJAX and no client-side filtering — the URL is the single source of truth,
 * so results are shareable, bookmarkable and back-button safe.
 */
(function () {
  function PlpFilters(root) {
    this.root = root;
    this.popup = root.querySelector('[data-plp-popup]');
    this.wrap = root.querySelector('.plp-filters');
    if (!this.popup || !this.wrap) return;

    this.form = this.popup.querySelector('[data-plp-form]');
    this.groupsEl = this.form || this.popup.querySelector('.plp-filters__groups');
    this.countEl = this.popup.querySelector('[data-count]');
    this.openBtns = Array.prototype.slice.call(root.querySelectorAll('[data-plp-open]'));
    this.wire();
    this.updateCount();
  }

  PlpFilters.prototype.wire = function () {
    var self = this;

    this.openBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.open();
      });
    });
    this.popup.parentElement.querySelectorAll('[data-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.close();
      });
    });
    // Clear unticks everything straight away so the reset is visible. It only
    // navigates when a filter is actually applied — otherwise there is nothing to
    // clear and a reload would be pure cost. A modified click (new tab) is left
    // alone entirely.
    var clearBtn = this.popup.querySelector('[data-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        self.uncheckAll();
        if (window.location.search.indexOf('filter.') === -1) e.preventDefault();
      });
    }

    // Ticking only updates the count — nothing is applied until Apply is pressed.
    this.groupsEl.addEventListener('change', function (e) {
      if (!e.target || e.target.type !== 'checkbox') return;
      self.updateCount();
    });

    // Mobile accordion: tapping a group heading collapses/expands it (desktop stays open)
    this.groupsEl.addEventListener('click', function (e) {
      var head = e.target.closest('[data-toggle]');
      if (!head) return;
      if (window.matchMedia('(min-width: 750px)').matches) return;
      var group = head.closest('.plp-filters__group');
      var open = group.classList.toggle('is-open');
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.wrap.hasAttribute('hidden')) self.close();
    });
  };

  PlpFilters.prototype.open = function () {
    this.wrap.removeAttribute('hidden');
    document.body.classList.add('plp-filters-open');
    this.openBtns.forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
  };

  PlpFilters.prototype.close = function () {
    this.wrap.setAttribute('hidden', '');
    document.body.classList.remove('plp-filters-open');
    this.openBtns.forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
  };

  PlpFilters.prototype.uncheckAll = function () {
    this.groupsEl.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
      input.checked = false;
    });
    this.updateCount();
  };

  PlpFilters.prototype.updateCount = function () {
    if (!this.countEl) return;
    var n = this.groupsEl.querySelectorAll('input[type="checkbox"]:checked').length;
    this.countEl.textContent = n ? '(' + (n < 10 ? '0' + n : n) + ' selected)' : '';
  };

  // Sticky PLP tab bar: pin it just below the sticky header. The offset is the
  // rendered header height, measured live so it always sits below the header.
  function setupStickyBar() {
    var bar = document.querySelector('.plp__tabs');
    if (!bar) return;
    // Measure the VISIBLE header. On transparent-header pages `.section-header`
    // collapses to 0 (its content is absolutely positioned), so use the actual
    // header element, which reports the real height in every case.
    var header =
      document.querySelector('.header-wrapper') ||
      document.querySelector('header.header') ||
      document.querySelector('sticky-header') ||
      document.querySelector('.section-header');

    function headerHeight() {
      var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      if (!h) {
        var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        h = parseInt(v, 10) || 0;
      }
      return h;
    }

    function update() {
      document.documentElement.style.setProperty('--plp-sticky-top', headerHeight() + 'px');
    }

    update();
    window.addEventListener('load', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.addEventListener('scroll', update, { passive: true });
    if (window.ResizeObserver && header) {
      new ResizeObserver(update).observe(header);
    }
    // Re-measure after fonts/logo settle (header height can change post-load).
    setTimeout(update, 300);
    setTimeout(update, 1200);
  }

  // Hide the fixed (mobile) Filters button once the NEXT section (after the PLP)
  // scrolls into view — so it tucks away as you reach the following content, and
  // reappears when you scroll back into the products (like the sticky header).
  function setupButtonVisibility(root) {
    var section = root.closest('.shopify-section') || root;
    var next = section.nextElementSibling;
    while (next && next.offsetHeight === 0) next = next.nextElementSibling;
    if (!next) return;

    var ticking = false;
    function check() {
      ticking = false;
      var r = next.getBoundingClientRect();
      // next section has entered the viewport (its top crossed the bottom edge)
      var nextInView = r.top < window.innerHeight && r.bottom > 0;
      root.classList.toggle('plp-out-of-view', nextInView);
    }
    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(check);
      }
    }
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function init() {
    setupStickyBar();
    document.querySelectorAll('[data-plp-filters]').forEach(function (root) {
      new PlpFilters(root);
      setupButtonVisibility(root);
    });
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
